import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/layout/AppLayout";
import EditDashboardHeader from "./EditDashboardHeader";
import EditLayoutPage, { LAYOUT_COLUMN_COUNT } from "./EditLayoutPage";
import { LayoutType } from "./LayoutContainer";
import Drawer from "@/components/common/Drawer";
import { LayoutCard } from "./EditLayoutPage";
import { useFetchCard } from "@/hooks/useFetchCard";
import { MESSAGES } from "@/constants/messages";
import { routes } from "@/utils/urls";
import api from "@/axios";
import { useToast } from "@/hooks/use-toast";
import { Spinner } from "@/components/ui/spinner";
import { Typography } from "../ui/typography";
import ToasterLight from "@/assets/svg/ToasterLight.svg";
import { useAppSelector, useAppDispatch } from "@/store/hook";
import { setHasPreferredLayout, setUserDashboard } from "@/store/slices/dashboardSlice";
import { setHasPreferredLayout as persistHasPreferredLayout } from "@/utils/localStorage";
import { ROUTE_PATH } from "@/constants/routeConstants";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import PreferredLayoutArrowLight from "@/assets/svg/ModalPreferredLight.svg";
import UpdatePopUpWarning from "@/assets/svg/UpdatePop-UpWarning.svg";
import EditLightIcon from "@/assets/svg/EditLightIcon.svg";
import EditDarkIcon from "@/assets/svg/EditDarkIcon.svg";

// Generates a unique ID for a card instance so dnd-kit can track each card
// reliably across layout transitions.  Uses the card type as a readable prefix
// so IDs remain debuggable, then appends a timestamp + short random suffix to
// guarantee uniqueness even if the same card type is re-added after removal.
const generateInstanceId = (cardTypeId: string): string =>
  `${cardTypeId}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Strips volatile fields (instanceId, preferredColumn) from the columns map so
// that change-detection compares only what matters semantically: which card types
// appear in which column and in what order.  This prevents a false "has changes"
// when a card is removed and re-added to the same position (which would generate
// a new instanceId and make a raw JSON.stringify comparison always differ).
function normalizeColumnsForComparison(cols: Record<string, LayoutCard[]>): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(cols).map(([colId, cards]) => [colId, cards.map((c) => c.id)])
  );
}

export interface UserDashboardCard {
  cardId: string;
  layoutPosition: string;
  order: number;
}

export interface UpdateUserDashboardPayload {
  layoutType: string;
  cards: UserDashboardCard[];
}

// ---------------------------------------------------------------------------
// Shared helper — converts a raw API dashboard + cards payload into the local
// edit-canvas state (LayoutType + column map).  Used both on initial load and
// when toggling the active layout mid-session.
// ---------------------------------------------------------------------------
function transformApiDataToEditState(
  dashboard: { layoutType?: string } | null | undefined,
  apiCardsData: any[] | null | undefined
): { newLayout: LayoutType; newColumns: Record<string, LayoutCard[]> } {
  const backendLayout = dashboard?.layoutType || "100";
  const newLayout = (backendLayout === "3-col" ? "3-column" : backendLayout) as LayoutType;

  const newColumns: Record<string, LayoutCard[]> = {
    "col-1": [],
    "col-2": [],
    "col-3": [],
  };

  // Sort by the saved order so cards render in the same sequence as persisted.
  const sorted = [...(apiCardsData || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  sorted.forEach((apiCard: any) => {
    const pos = apiCard.layoutPosition;
    // Fixed cards are rendered separately above the canvas — skip them here.
    if (pos === "fixed") return;
    const colIndex = pos === "2" ? 2 : pos === "3" ? 3 : 1;
    const colId = `col-${colIndex}`;
    if (!newColumns[colId]) return;

    const frontendId = apiCard?.type || apiCard?.cardId || "";

    newColumns[colId].push({
      instanceId: generateInstanceId(frontendId),
      id: frontendId,
      label: apiCard?.displayTitle || "-",
      preferredColumn: colId,
    });
  });

  return { newLayout, newColumns };
}

// ---------------------------------------------------------------------------
// Layout-transition utility driven by each card's preferredColumn.
//
// Rules:
//   - If a card's preferredColumn exists in the new layout → place it there.
//   - If a card's preferredColumn would exceed the new column count (contraction)
//     → fall back to col-1, appended below existing col-1 cards.
//   - Cards are processed column-by-column (col-1 → col-2 → col-3) so
//     within-column ordering is always preserved.
//   - Same-count proportion switches (50-50 ↔ 70-30 ↔ 30-70) leave every
//     card exactly where it is — no redistribution needed.
//
// Because each card remembers its preferred column, switching back to a
// wider layout restores manual placements automatically — no snapshots needed.
// ---------------------------------------------------------------------------
function transformColumnsWithPreference(
  current: Record<string, LayoutCard[]>,
  toCount: number,
): Record<string, LayoutCard[]> {
  const result: Record<string, LayoutCard[]> = {
    "col-1": [],
    "col-2": [],
    "col-3": [],
  };

  for (let i = 1; i <= 3; i++) {
    const colId = `col-${i}`;
    for (const card of (current[colId] ?? [])) {
      const preferred = card.preferredColumn ?? "col-1";
      const preferredIndex = parseInt(preferred?.split("-")?.[1] ?? "1");
      // Use preferredColumn if it fits in the new layout, otherwise overflow to col-1.
      const targetCol = preferredIndex <= toCount ? preferred : "col-1";
      result[targetCol].push(card);
    }
  }

  return result;
}

const EditLayout = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const selectedPatient = useAppSelector((state) => state.search.selectedPatient);
  const dashboardRoute = selectedPatient?.memberId
    ? `${ROUTE_PATH.ROUTE_DASHBOARD}/${encodeURIComponent(selectedPatient.memberId)}`
    : ROUTE_PATH.ROUTE_DASHBOARD;
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdateConfirmOpen, setIsUpdateConfirmOpen] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [layout, setLayout] = useState<LayoutType>("100");
  const [isToggling, setIsToggling] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoadingLayout, setIsLoadingLayout] = useState(false);

  // Guard so the preferred-layout fetch only fires once per mount, even if
  // hasPreferredLayout changes value while the component is alive.
  const hasFetchedRef = useRef(false);

  // Snapshot of the layout state as it existed when the canvas was first
  // populated (either from the API fetch or the default empty state).
  // hasChanges compares live state against this to enable/disable Save.
  const initialStateRef = useRef<{ layout: LayoutType; columns: Record<string, LayoutCard[]> }>({
    layout: "100",
    columns: { "col-1": [], "col-2": [], "col-3": [] },
  });

  // Read the user's current dashboard state from Redux (persisted across sessions).
  const dashboardState = useAppSelector((state) => state.dashboard);
  const currentDashboardType = dashboardState.dashboard?.dashboardType ?? "default";
  const hasPreferredLayout = dashboardState.hasPreferredLayout ?? false;
  const userId = dashboardState.dashboard?.userId;

  // Mutually exclusive restore options — see RestoreLayoutDialogBox for full logic.
  const showDefaultRestore = !hasPreferredLayout || currentDashboardType === "preferred";
  const showPreferredRestore = hasPreferredLayout && currentDashboardType === "default";

  // ---------------------------------------------------------------------------
  // On mount: if the user has a saved preferred layout AND is currently viewing
  // the preferred layout, fetch it and pre-fill the canvas so they are editing
  // their actual saved configuration.
  //
  // When the user is on the Default Layout, we intentionally skip the fetch so
  // the canvas opens with a clean 100% single-column empty state — giving a
  // clear starting point rather than loading someone else's preferred layout.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!hasPreferredLayout || hasFetchedRef.current || currentDashboardType === "default") return;
    hasFetchedRef.current = true;

    const fetchPreferredLayout = async () => {
      setIsLoadingLayout(true);
      try {
        const response = await api.get(routes.editLayout.preferredLayout);
        const responseData = response.data?.data;

        if (responseData) {
          const { dashboard, cards: apiCardsData } = responseData;
          const { newLayout, newColumns } = transformApiDataToEditState(dashboard, apiCardsData);
          setLayout(newLayout);
          setColumns(newColumns);
          // Snapshot the loaded state so hasChanges starts as false.
          initialStateRef.current = { layout: newLayout, columns: newColumns };
        }
      } catch (error) {
        console.error("Failed to load preferred layout for editing:", error);
        // Non-fatal — canvas stays empty so the user can build from scratch.
      } finally {
        setIsLoadingLayout(false);
      }
    };

    fetchPreferredLayout();
  }, [hasPreferredLayout]);

  const { data: apiCards, loading } = useFetchCard<any[]>({
    endpoint: routes.editLayout.getAllCards,
    enabled: true,
  });

  // Demographics and Care Management are always rendered as fixed cards in
  // EditLayoutPage (hardcoded), so they must never appear in the drawer.
  const FIXED_CARD_TYPES = new Set(["DEMOGRAPHICS", "CARE_MANAGEMENT"]);

  const drawerApiCards = useMemo(() => {
    if (!apiCards) return apiCards;
    return apiCards.filter(
      (c: any) =>
        c?.layoutPosition !== "fixed" &&
        !FIXED_CARD_TYPES.has(c?.type)
    );
  }, [apiCards]);

  const cardIdToDisplayTitle = useMemo(() => {
    const map: Record<string, string> = {};
    apiCards?.forEach((apiCard: any) => {
      const id = apiCard?.type || apiCard?.displayTitle;
      if (id) map[id] = apiCard?.displayTitle || "-";
    });
    return map;
  }, [apiCards]);

  const [columns, setColumns] = useState<Record<string, LayoutCard[]>>(() =>
    Object.fromEntries(
      Array.from({ length: 3 }, (_, i) => [`col-${i + 1}`, [] as LayoutCard[]])
    )
  );

  const addedCardIds = Object.values(columns).flat().map(card => card.id);

  // True only when the user has made a meaningful change relative to the
  // initial loaded state (add / remove / reorder cards, or layout type change).
  const hasChanges = useMemo(() => {
    // Default layout: Save is always enabled — the user is building from scratch.
    if (currentDashboardType === "default") return true;
    // If the layout type itself changed from the initial saved layout, enable Save
    // so the user can persist the new layout type even with the same fixed cards.
    if (layout !== initialStateRef.current.layout) return true;
    // Same layout type: check whether the card arrangement changed.
    const toCount = LAYOUT_COLUMN_COUNT[layout];
    const initialInCurrentLayout = transformColumnsWithPreference(
      initialStateRef.current.columns,
      toCount
    );
    return JSON.stringify(normalizeColumnsForComparison(columns)) !== JSON.stringify(normalizeColumnsForComparison(initialInCurrentLayout));
  }, [columns, currentDashboardType, layout]);

  const handleLayoutChange = (newLayout: LayoutType) => {
    // Guard: re-selecting the same layout is a no-op.
    if (newLayout === layout) return;

    const toCount = LAYOUT_COLUMN_COUNT[newLayout];

    // Each card carries a preferredColumn set when it was last dragged.
    // transformColumnsWithPreference routes every card to its preferred column
    // if that column exists in the new layout, or falls back to col-1 otherwise.
    // This preserves manual drag placements across all layout transitions without
    // needing snapshots.
    setLayout(newLayout);
    setColumns(transformColumnsWithPreference(columns, toCount));
  };

  const addCard = (cardId: string) => {
    const newCard: LayoutCard = {
      instanceId: generateInstanceId(cardId),
      id: cardId,
      label: cardIdToDisplayTitle[cardId] || cardId,
      preferredColumn: "col-1",
    };
    setColumns(prev => ({
      ...prev,
      "col-1": [...(prev?.["col-1"] ?? []), newCard],
    }));
  };

  const removeCard = (cardId: string) => {
    setColumns(prev => {
      const newColumns = { ...prev };
      for (const colId in newColumns) {
        newColumns[colId] = newColumns[colId].filter(card => card.id !== cardId);
      }
      return newColumns;
    });
  };

  const handleSave = async () => {
    const cards: UserDashboardCard[] = [];

    const cardIdToUuidMap: Record<string, string> = {};
    apiCards?.forEach((apiCard: any) => {
      const normalizedId = apiCard?.type || apiCard?.displayTitle;
      cardIdToUuidMap[normalizedId] = apiCard.id;
    });

    const LAYOUT_POSITIONS: Record<number, string[]> = {
      1: ["1"],
      2: ["1", "2"],
      3: ["1", "2", "3"],
    };

    const activeColumnCount = LAYOUT_COLUMN_COUNT[layout] || 1;
    const positionNames = LAYOUT_POSITIONS[activeColumnCount] || LAYOUT_POSITIONS[1];

    Object.entries(columns).forEach(([colId, colCards]) => {
      const colIndex = parseInt(colId?.split("-")?.[1] ?? "1") - 1;
      if (colIndex >= activeColumnCount) return;

      const layoutPosition = positionNames?.[colIndex] ?? "1";
      colCards.forEach((card, index) => {
        const cardId = cardIdToUuidMap?.[card.id] ?? card.id;
        cards.push({ cardId, layoutPosition, order: index + 1 });
      });
    });

    const payload: UpdateUserDashboardPayload = {
      layoutType: layout === "3-column" ? "3-col" : layout,
      cards,
    };

    setIsSaving(true);
    try {
      await api.put(routes.editLayout.updateLayout, payload);

      // Explicitly activate the preferred layout after saving
      const activeResponse = await api.patch(routes.editLayout.activeLayout, { activeLayout: "preferred" });
      const activeResponseData = activeResponse.data?.data;
      if (activeResponseData) {
        const { dashboard, cards: apiCardsData } = activeResponseData;
        dispatch(setUserDashboard({ dashboard, cards: apiCardsData }));
      }

      // Mark that a preferred layout now exists so the Restore menu and the
      // initial-load effect stay accurate across sessions.
      dispatch(setHasPreferredLayout(true));
      if (userId) {
        persistHasPreferredLayout(userId, true);
      }

      toast({
        description: (
          <div className="flex items-center gap-3">
            <img src={ToasterLight} alt="success" className="w-6 h-6 flex-shrink-0" />
            <Typography variant="medium-16" className="text-grey-800 dark:text-white whitespace-nowrap">
              {MESSAGES.preferredLayoutSaved}
            </Typography>
          </div>
        ),
        duration: 2500,
      });

      navigate(dashboardRoute);
    } catch (error) {
      console.error("Failed to update dashboard layout:", error);
      toast({
        title: "Error",
        description: "Failed to save dashboard layout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
      setIsUpdateConfirmOpen(false);
    }
  };

  const handleToggleActive = async (type: "default" | "preferred") => {
    setIsToggling(true);
    try {
      const response = await api.patch(routes.editLayout.activeLayout, { activeLayout: type });
      const responseData = response.data?.data;

      if (responseData) {
        const { dashboard, cards: apiCardsData } = responseData;
        const { newLayout, newColumns } = transformApiDataToEditState(dashboard, apiCardsData);
        setLayout(newLayout);
        setColumns(newColumns);
        dispatch(setUserDashboard({ dashboard, cards: apiCardsData }));
      }

      navigate(dashboardRoute);
    } catch (error) {
      console.error("Failed to toggle active layout:", error);
      toast({
        title: "Error",
        description: "Failed to switch active layout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsToggling(false);
    }
  };

  const handleSaveClick = () => {
    if (hasPreferredLayout) {
      setIsUpdateConfirmOpen(true);
    } else {
      handleSave();
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setIsCancelConfirmOpen(true);
    } else {
      navigate(dashboardRoute);
    }
  };

  // Show a full-canvas spinner while the preferred layout is being fetched on
  // initial load, so the user never sees a flash of empty columns.
  const isBlockingLoad = isLoadingLayout;

  return (
    <>
    <ConfirmationModal
      isOpen={isCancelConfirmOpen}
      onClose={() => setIsCancelConfirmOpen(false)}
      title="Discard Changes"
      description="You have unsaved changes."
      subText="Are you sure you want to exit Edit Layout? Any unsaved changes will be lost."
      buttons={[
        {
          label: "Keep Editing",
          onClick: () => setIsCancelConfirmOpen(false),
          variant: "outline",
        },
        {
          label: "Discard Changes",
          onClick: () => navigate(dashboardRoute),
          variant: "contained1",
        },
      ]}
    />
    <ConfirmationModal
      isOpen={isUpdateConfirmOpen}
      onClose={() => !isSaving && setIsUpdateConfirmOpen(false)}
      title="Update My Layout"
      description="Do you want to update My Layout?"
      subText="This will overwrite your saved My Layout with your updated My Layout."
      modalWidth="606px"
      showWarning
      warningText="Note: The previous layout cannot be restored once it has been overwritten."
      warningIcon={UpdatePopUpWarning}
      buttons={[
        {
          label: "Cancel",
          onClick: () => setIsUpdateConfirmOpen(false),
          variant: "outline",
          className: isSaving ? "pointer-events-none opacity-50" : "",
        },
        {
          label: "Save as My Layout",
          onClick: () => {
            handleSave();
          },
          variant: "contained1",
          icon: PreferredLayoutArrowLight,
          isLoading: isSaving,
        },
      ]}
    />
    <AppLayout
      fullWidth
      subHeaderContent={
        <EditDashboardHeader
          layout={layout}
          onLayoutChange={handleLayoutChange}
          onToggleDrawer={() => setIsDrawerOpen(true)}
          hasChanges={hasChanges}
          onCancel={handleCancel}
          onSave={handleSaveClick}
          isSaving={isSaving}
          isLoading={isLoadingLayout}
          onRestore={handleToggleActive}
          isToggling={isToggling}
          showDefaultRestore={showDefaultRestore}
          showPreferredRestore={showPreferredRestore}
          hasPreferredLayout={hasPreferredLayout}
        />
      }
    >
      <div className="flex w-full h-full overflow-hidden relative">
        {/* Blocking overlay during initial preferred-layout fetch or save.
            Uses fixed positioning so it covers the full viewport including
            the subheader, and all header controls are disabled via isLoading. */}
        {isBlockingLoad && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/60 dark:bg-[#1a2128]/60 backdrop-blur-[1px]">
            <Spinner size="lg" className="text-primary-600" />
          </div>
        )}
        <div className="flex-1 min-w-0 flex flex-col h-full overflow-hidden transition-all duration-500 ease-in-out">
          {/* Banner — fixed above scroll area, always visible */}
          <div className={`flex-shrink-0 pt-4 pb-4 pl-4 md:pl-[40px] transition-[padding-right] duration-500 ease-in-out ${isDrawerOpen ? "pr-4" : "pr-4 md:pr-[40px]"}`}>
            <div className="flex items-center h-[50px] bg-warning-100 dark:bg-dark-blue-700 dark:border dark:border-warning-500 px-4 gap-3 rounded-md">
              <img src={EditLightIcon} alt="edit mode" className="w-[26px] h-[26px] dark:hidden" />
              <img src={EditDarkIcon} alt="edit mode" className="w-[26px] h-[26px] hidden dark:block" />
              <Typography variant="medium-14" className="text-banner-variant dark:text-grey-100 flex-1">
                Edit Mode active. Data shown is placeholder. Click 'Save' to save changes and return to the dashboard.
              </Typography>
            </div>
          </div>
          {/* Scrollable canvas */}
          <div className={`flex-1 overflow-y-auto custom-scrollbar flex flex-col pl-4 pb-4 md:pl-[40px] transition-[padding-right] duration-500 ease-in-out ${isDrawerOpen ? "pr-4" : "pr-4 md:pr-[40px]"}`}>
            <EditLayoutPage layout={layout} columns={columns} setColumns={setColumns} />
          </div>
        </div>
        <div className={`transition-all duration-500 ease-in-out overflow-hidden flex-shrink-0 ${isDrawerOpen ? "w-[500px] min-w-[500px]" : "w-0 min-w-0"}`}>
          <div className="w-[500px] h-full">
            <Drawer
              onClose={() => setIsDrawerOpen(false)}
              addedCardIds={addedCardIds}
              onAdd={addCard}
              onRemove={removeCard}
              apiCards={drawerApiCards}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </AppLayout>
    </>
  );
};

export default EditLayout;
